/**
 * Hero slides (sections/hero-slides.liquid).
 *
 * Every element marked [data-hs-index] belongs to one slide: its heading, its button and its set of product images.
 * Showing a slide marks its elements .is-active and the previous slide's .is-leaving, and CSS does the animation.
 * The progress bar under the active dot is a CSS animation as long as one slide; when it ends, the next slide shows.
 * Pausing (hover, keyboard focus, the pause button, the hero being off screen) just pauses that animation.
 * The images of slides 2 onwards are only loaded once the page has finished loading (see snippets/hero-slides-card.liquid),
 * so they don't compete with the first slide.
 */

class HeroSlides extends HTMLElement {
  connectedCallback() {
    this.fitUnderHeader();
    window.addEventListener('resize', () => this.fitUnderHeader());

    this.count = Number(this.dataset.count) || 0;
    this.index = 0;
    this.leaveTimer = 0;
    if (this.count < 2) return;

    // Load the other slides' images once the page has loaded (well before the first slide change)
    if (document.readyState === 'complete') this.loadAllSlides();
    else window.addEventListener('load', () => this.loadAllSlides(), { once: true });

    this.dots = [...this.querySelectorAll('.hs__dot')];
    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.show(i);
      })
    );

    this.addEventListener('animationend', (event) => {
      const target = /** @type {Element} */ (event.target);
      if (target.classList.contains('hs__dot-fill') && target.closest('.hs__dot.is-active')) this.show(this.index + 1);
    });

    this.pauseButton = this.querySelector('.hs__pause');
    this.pauseButton?.addEventListener('click', () => this.setStopped(!this.classList.contains('is-stopped')));

    // Pause while a mouse is over the hero or keyboard focus is inside it
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.classList.add('is-hover');
    });
    this.addEventListener('pointerleave', () => this.classList.remove('is-hover'));
    this.addEventListener('focusin', () => this.classList.add('is-focus'));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.classList.remove('is-focus');
    });

    // Pause while scrolled away, so visitors come back to the slide they left
    this.observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-offscreen', !entry?.isIntersecting);
    });
    this.observer.observe(this);

    this.addSwipe();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.setStopped(true);

    // Theme editor: selecting a slide block shows it and holds it there
    this.addEventListener('shopify:block:select', (event) => {
      const item = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('[data-hs-index]'));
      if (!item) return;
      this.classList.add('is-editing');
      this.show(Number(item.dataset.hsIndex));
    });
    this.addEventListener('shopify:block:deselect', () => this.classList.remove('is-editing'));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * As the first section under a see-through header, the header (and announcement bar) cover the top of the hero.
   * Measure how far they reach into it and pad the top by exactly that; the stylesheet has a rougher fallback.
   * Only measured at the top of the page, where the header sits in its normal place.
   */
  fitUnderHeader() {
    const section = this.closest('.shopify-section');
    const transparent = getComputedStyle(document.body).getPropertyValue('--transparent-header-offset-boolean').trim() === '1';
    const header = document.querySelector('#header-component');
    if (!section?.matches(':first-child') || !transparent || !header || window.scrollY > 0) return;

    const overlap = Math.round(header.getBoundingClientRect().bottom - this.getBoundingClientRect().top);
    if (overlap > 0) this.style.paddingBlockStart = `calc(var(--padding-block-start, 0px) + ${overlap}px)`;
  }

  /** Turn the waiting images of slides 2 onwards into real images */
  loadAllSlides() {
    this.querySelectorAll('img[data-hs-src]').forEach((image) => {
      const srcset = image.getAttribute('data-hs-srcset');
      if (srcset) image.setAttribute('srcset', srcset);
      image.setAttribute('src', image.getAttribute('data-hs-src') || '');
      image.removeAttribute('data-hs-src');
      image.removeAttribute('data-hs-srcset');
    });
  }

  /** @param {boolean} stopped */
  setStopped(stopped) {
    this.classList.toggle('is-stopped', stopped);
    if (!this.pauseButton) return;
    this.pauseButton.setAttribute('aria-pressed', String(stopped));
    this.pauseButton.setAttribute('aria-label', stopped ? 'Play slideshow' : 'Pause slideshow');
  }

  /** @param {number} next */
  show(next) {
    this.loadAllSlides();
    next = ((next % this.count) + this.count) % this.count;
    if (next === this.index) return;
    const previous = this.index;
    this.index = next;

    const itemsOf = (/** @type {number} */ i) => this.querySelectorAll(`[data-hs-index="${i}"]`);
    const prevHeading = this.querySelector(`.hs__heading[data-hs-index="${previous}"] .hs__line--top`);
    const nextHeading = this.querySelector(`.hs__heading[data-hs-index="${next}"] .hs__line--top`);
    const sameTop = prevHeading && nextHeading && prevHeading.textContent?.trim() === nextHeading.textContent?.trim();

    this.querySelectorAll('.is-leaving').forEach((element) => element.classList.remove('is-leaving', 'keep-top'));

    itemsOf(previous).forEach((element) => {
      element.classList.remove('is-active');
      element.classList.add('is-leaving');
      element.classList.toggle('keep-top', Boolean(sameTop));
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    });

    itemsOf(next).forEach((element) => {
      element.classList.remove('is-leaving');
      element.classList.add('is-active');
      element.classList.toggle('keep-top', Boolean(sameTop));
      element.removeAttribute('aria-hidden');
      element.removeAttribute('inert');
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === next);
      if (i === next) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const set = /** @type {HTMLElement | null} */ (this.querySelector(`.hs__set[data-hs-index="${next}"]`));
    if (set?.dataset.glow) this.style.setProperty('--hs-glow', set.dataset.glow);
    else this.style.removeProperty('--hs-glow');

    clearTimeout(this.leaveTimer);
    this.leaveTimer = window.setTimeout(() => {
      this.querySelectorAll('.is-leaving').forEach((element) => element.classList.remove('is-leaving', 'keep-top'));
      this.querySelectorAll('.keep-top').forEach((element) => element.classList.remove('keep-top'));
    }, 900);
  }

  /** Swipe left or right on touch screens to change slides */
  addSwipe() {
    let startX = 0;
    let startY = 0;
    this.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        startX = touch.clientX;
        startY = touch.clientY;
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.show(this.index + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
  }
}

if (!customElements.get('hero-slides')) customElements.define('hero-slides', HeroSlides);